'use client'

import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { useToast } from "@/hooks/use-toast"
import { ArrowLeft, Loader2, Search, PlusCircle, CheckCircle, XCircle, BarChart3, Save } from "lucide-react"
import ChartOfAccountsService from '@/api/chartofaccounts.service'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { AccountType, ChartOfAccount, AccountSubtype } from "@/types/index"

interface BusinessChartOfAccount {
  id: number
  businessId: number
  chartOfAccountId: number
  chartOfAccount?: ChartOfAccount
  isActive: boolean
  timezone: string
  dateUpdated?: string
  dateCreated: string
}

const accountTypes: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

// Map each account type to its possible subtypes
const accountSubtypes: Record<AccountType, AccountSubtype[]> = {
  'ASSET': ['Current Asset', 'Non-Current Asset', 'Cash and Cash Equivalents', 'Accounts Receivable', 'Inventory', 'Fixed Assets'],
  'LIABILITY': ['Current Liability', 'Non-Current Liability', 'Accounts Payable', 'Loans', 'Taxes Payable'],
  'EQUITY': ['Owner Equity', 'Retained Earnings', 'Capital'],
  'INCOME': ['Sales', 'Other Income', 'Revenue', 'Discount'],
  'EXPENSE': ['Cost of Sales', 'Operating Expense', 'Other Expense', 'Salaries', 'Rent', 'Utilities', 'Advertising'],
};

// Map account types to their normal balance
const normalBalanceMap: Record<AccountType, 'DEBIT' | 'CREDIT'> = {
  'ASSET': 'DEBIT',
  'EXPENSE': 'DEBIT',
  'LIABILITY': 'CREDIT',
  'EQUITY': 'CREDIT',
  'INCOME': 'CREDIT',
};

function typeColor(type: AccountType) {
  switch (type) {
    case 'ASSET': return 'bg-emerald-100 text-emerald-800';
    case 'LIABILITY': return 'bg-red-100 text-red-800';
    case 'EQUITY': return 'bg-indigo-100 text-indigo-800';
    case 'INCOME': return 'bg-blue-100 text-blue-800';
    case 'EXPENSE': return 'bg-amber-100 text-amber-800';
    default: return 'bg-slate-100 text-slate-800';
  }
}

export default function BusinessChartOfAccounts() {
  const { toast } = useToast()
  const [isLoadingBusiness, setIsLoadingBusiness] = useState(true)
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(true)
  const [businessAccounts, setBusinessAccounts] = useState<BusinessChartOfAccount[]>([])
  const [availableAccounts, setAvailableAccounts] = useState<ChartOfAccount[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [assigningAccounts, setAssigningAccounts] = useState<Record<number, boolean>>({})
  const [isRemoving, setIsRemoving] = useState(false)
  const [activeTab, setActiveTab] = useState('business')
  const [isSearching, setIsSearching] = useState(false)
  
  // Form states for creating new chart of account
  const [isCreating, setIsCreating] = useState(false)
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: '' as AccountType | '',
    subtype: '',
    description: '',
    isActive: true,
    defaultAccount: false,
    allowSubAccounts: false,
    isSubAccount: false,
    parentId: null as number | null,
  })
  
  // Statistics state
  const [accountStats, setAccountStats] = useState<{
    [key in AccountType]: { count: number; active: number; inactive: number }
  }>({
    ASSET: { count: 0, active: 0, inactive: 0 },
    LIABILITY: { count: 0, active: 0, inactive: 0 },
    EQUITY: { count: 0, active: 0, inactive: 0 },
    INCOME: { count: 0, active: 0, inactive: 0 },
    EXPENSE: { count: 0, active: 0, inactive: 0 }
  })
  
  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  })

  useEffect(() => {
    if (selectedBusiness?.id) {
      fetchBusinessAccounts()
      fetchAvailableAccounts()
    }
  }, [selectedBusiness])

  useEffect(() => {
    if (selectedBusiness?.id) {
      const timeoutId = setTimeout(() => {
        if (searchTerm.trim()) {
          handleSearch()
        } else {
          // Reset to original data when search is cleared
          fetchBusinessAccounts()
          fetchAvailableAccounts()
        }
      }, 300) // Debounce search

      return () => clearTimeout(timeoutId)
    }
  }, [searchTerm, selectedBusiness])

  const handleSearch = async () => {
    if (!searchTerm.trim()) return
    
    setIsSearching(true)
    try {
      // Search both business and available accounts
      const [businessResponse, availableResponse] = await Promise.all([
        ChartOfAccountsService.searchChartOfAccounts(searchTerm, 'business', selectedBusiness.id),
        ChartOfAccountsService.searchChartOfAccounts(searchTerm, 'default')
      ])

      if (businessResponse.data.result === 'success') {
        const accounts = businessResponse.data.businessChartOfAccounts || businessResponse.data.chartOfAccounts || [];
        setBusinessAccounts(accounts)
        calculateStats(accounts)
      }

      if (availableResponse.data.result === 'success') {
        setAvailableAccounts(availableResponse.data.defaultChartOfAccounts || availableResponse.data.chartOfAccounts || [])
      }
    } catch (error) {
      console.error("Error searching chart of accounts:", error)
      toast({
        title: "Search Error",
        description: "Failed to search chart of accounts",
        variant: "destructive",
      })
    } finally {
      setIsSearching(false)
    }
  }

  const calculateStats = (accounts: BusinessChartOfAccount[]) => {
    const stats = {
      ASSET: { count: 0, active: 0, inactive: 0 },
      LIABILITY: { count: 0, active: 0, inactive: 0 },
      EQUITY: { count: 0, active: 0, inactive: 0 },
      INCOME: { count: 0, active: 0, inactive: 0 },
      EXPENSE: { count: 0, active: 0, inactive: 0 }
    };
    
    accounts.forEach((account: BusinessChartOfAccount) => {
      if (account.chartOfAccount) {
        const type = account.chartOfAccount.type as AccountType;
        if (stats[type]) {
          stats[type].count++;
          if (account.isActive) {
            stats[type].active++;
          } else {
            stats[type].inactive++;
          }
        }
      }
    });
    
    setAccountStats(stats);
  }

  const fetchBusinessAccounts = async () => {
    setIsLoadingBusiness(true)
    try {
      const response = await ChartOfAccountsService.getAllBusinessChartOfAccounts(selectedBusiness.id)
      if (response.data.result === 'success') {
        const accounts = response.data.businessChartOfAccounts;
        setBusinessAccounts(accounts)
        calculateStats(accounts)
      } else {
        toast({
          title: "Error",
          description: "Failed to load business chart of accounts",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Error fetching business chart of accounts:", error)
      toast({
        title: "Error",
        description: "Failed to load business chart of accounts",
        variant: "destructive",
      })
    } finally {
      setIsLoadingBusiness(false)
    }
  }

  const fetchAvailableAccounts = async () => {
    setIsLoadingAvailable(true)
    try {
      // Get all default chart of accounts that can be assigned to the business
      const response = await ChartOfAccountsService.getAllDefaultChartOfAccounts()
      console.log("getAllDefaultChartOfAccounts response.data", response.data)
      if (response.data.result === 'success') {
        setAvailableAccounts(response.data.defaultChartOfAccounts)
      } else {
        toast({
          title: "Error",
          description: "Failed to load available chart of accounts",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Error fetching available chart of accounts:", error)
      toast({
        title: "Error",
        description: "Failed to load available chart of accounts",
        variant: "destructive",
      })
    } finally {
      setIsLoadingAvailable(false)
    }
  }

  const createBusinessChartOfAccount = async (chartOfAccountId: number) => {
    setAssigningAccounts(prev => ({ ...prev, [chartOfAccountId]: true }))
    try {
      const data = {
        chartOfAccountId: chartOfAccountId,
        isActive: true
      }
      
      const response = await ChartOfAccountsService.createBusinessChartOfAccount(
        selectedBusiness.id, 
        data
      )
      
      if (response.data.result === 'success') {
        toast({
          title: "Account Assigned",
          description: "Chart of account has been assigned to your business",
        })
        // Refresh the business accounts list
        fetchBusinessAccounts()
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to assign chart of account",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Error assigning chart of account:", error)
      toast({
        title: "Error",
        description: "An error occurred while assigning the chart of account",
        variant: "destructive",
      })
    } finally {
      setAssigningAccounts(prev => ({ ...prev, [chartOfAccountId]: false }))
    }
  }

  const removeAccountFromBusiness = async (chartOfAccountId: string) => {
    setIsRemoving(true)
    try {
      const response = await ChartOfAccountsService.removeChartOfAccountFromBusiness(
        selectedBusiness.id, 
        chartOfAccountId
      )
      
      if (response.data.result === 'success') {
        toast({
          title: "Account Removed",
          description: "Chart of account has been removed from your business",
        })
        // Refresh the business accounts list
        fetchBusinessAccounts()
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to remove chart of account",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Error removing chart of account:", error)
      toast({
        title: "Error",
        description: "An error occurred while removing the chart of account",
        variant: "destructive",
      })
    } finally {
      setIsRemoving(false)
    }
  }

  // Check if an account is already assigned to business
  const isAccountAssigned = (accountId: number) => {
    return businessAccounts.some(businessAccount => 
      businessAccount.chartOfAccount?.id === accountId || 
      businessAccount.chartOfAccountId === accountId
    )
  }

  console.log("businessAccounts", businessAccounts);
  console.log("availableAccounts", availableAccounts);

  const handleTypeChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      type: value as AccountType,
      subtype: '' // Reset subtype when type changes
    }))
  }

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      type: '',
      subtype: '',
      description: '',
      isActive: true,
      defaultAccount: false,
      allowSubAccounts: false,
      isSubAccount: false,
      parentId: null,
    })
  }

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.code || !formData.name || !formData.type) {
      toast({
        title: 'Required Fields Missing',
        description: 'Please fill in code, name, and type fields',
        variant: 'destructive',
      })
      return
    }
    
    setIsCreating(true)
    try {
      const accountData = {
        code: formData.code,
        name: formData.name,
        type: formData.type,
        subtype: formData.subtype || undefined,
        description: formData.description || undefined,
        isActive: formData.isActive,
        defaultAccount: formData.defaultAccount,
        allowSubAccounts: formData.allowSubAccounts,
        isSubAccount: formData.isSubAccount,
        parentId: formData.parentId,
        normalBalance: normalBalanceMap[formData.type as AccountType],
      }
      
      const response = await ChartOfAccountsService.createChartOfAccount(accountData, selectedBusiness.id)
      
      if (response.data.result === 'success') {
        toast({
          title: 'Account Created',
          description: 'Chart of account has been created successfully',
        })
        
        // Reset form and close dialog
        resetForm()
        setIsAddDialogOpen(false)
        
        // Refresh the business accounts list
        fetchBusinessAccounts()
        fetchAvailableAccounts()
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Failed to create chart of account',
          variant: 'destructive',
        })
      }
    } catch (error) {
      console.error('Error creating chart of account:', error)
      toast({
        title: 'Error',
        description: 'An error occurred while creating the chart of account',
        variant: 'destructive',
      })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            onClick={() => window.history.back()} 
            variant="ghost" 
            size="icon" 
            className="rounded-full h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold">Chart of Accounts</h1>
          <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 hover:bg-blue-50">
            {businessAccounts.length} accounts
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setIsAddDialogOpen(true)} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <PlusCircle className="h-4 w-4" /> Add Account
          </Button>
        </div>
      </div>
      
      {/* Compliance Information */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-4">
          <div className="text-sm text-slate-700">
            <p className="font-medium mb-2">Chart of Accounts supports two compliance versions:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="font-medium">1. IFRS</span> - International Financial Reporting Standards (Private Sector)
              </div>
              <div>
                <span className="font-medium">2. IPSAS (ZFRM)</span> - International Public Sector Accounting Standards (Public Sector)
              </div>
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded-md">
              <h4 className="font-medium text-blue-900 mb-1">Zimbabwe Financial Reporting Manual (ZFRM)</h4>
              <p className="text-xs text-blue-800">
                MoyoTools aligns with the Zimbabwe Financial Reporting Manual (ZFRM), Zimbabwe's official framework for implementing 
                International Public Sector Accounting Standards (IPSAS). For public sector deployments, ZFRM compliance is mandatory.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {Object.entries(accountStats).map(([type, stats]) => (
          <Card key={type} className={`overflow-hidden border-t-4 ${typeColor(type as AccountType)}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">{type}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.count}</div>
              <div className="text-sm text-slate-500 mt-1">
                <span className="text-green-600">{stats.active} active</span>
                {stats.inactive > 0 && (
                  <span className="text-slate-400 ml-2">{stats.inactive} inactive</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <Tabs defaultValue="business" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="business">Selected Business Accounts</TabsTrigger>
          <TabsTrigger value="available">All Available Chart of Accounts</TabsTrigger>
        </TabsList>
        
        <div className="mb-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search accounts by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
            )}
          </div>
        </div>
        
        <TabsContent value="business" className="space-y-4">
          <Card className="shadow-sm border border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle>Business Chart of Accounts</CardTitle>
            </CardHeader>
            
            <Separator />
            
            <CardContent className="p-0">
              {isLoadingBusiness ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="ml-2 text-slate-600">Loading business accounts...</span>
                </div>
              ) : (
                <>
                  {businessAccounts.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">
                        {searchTerm ? 'No accounts found matching your search.' : 'No accounts found. Add accounts from the Available Accounts tab.'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200 dark:divide-slate-800">
                      {businessAccounts.map((item: any) => (
                        <div key={item.id} className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                                {item.chartOfAccount?.code || item.code}
                              </span>
                              <span className="font-medium">{item.chartOfAccount?.name || item.name}</span>
                              <Badge className={`${typeColor((item.chartOfAccount?.type || item.type) as AccountType)}`}>
                                {item.chartOfAccount?.type || item.type}
                              </Badge>
                            </div>
                            {(item.chartOfAccount?.description || item.description) && (
                              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                                {item.chartOfAccount?.description || item.description}
                              </p>
                            )}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => removeAccountFromBusiness(item.chartOfAccount?.publicId || item.publicId || '')}
                            disabled={isRemoving}
                          >
                            {isRemoving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 mr-1" /> Remove
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="available" className="space-y-4">
          <Card className="shadow-sm border border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle>Available Chart of Accounts</CardTitle>
            </CardHeader>
            
            <Separator />
            
            <CardContent className="p-0">
              {isLoadingAvailable ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="ml-2 text-slate-600">Loading available accounts...</span>
                </div>
              ) : (
                <>
                  {availableAccounts.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">
                        {searchTerm ? 'No accounts found matching your search criteria.' : 'No available accounts found.'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200 dark:divide-slate-800">
                      {availableAccounts.map((account) => {
                        const isAssigned = isAccountAssigned(account.id || 0);
                        return (
                          <div key={account.id} className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                                  {account.code}
                                </span>
                                <span className="font-medium">{account.name}</span>
                                <Badge className={`${typeColor(account.type as AccountType)}`}>
                                  {account.type}
                                </Badge>
                              </div>
                              {account.description && (
                                <p className="text-sm text-muted-foreground mt-1 max-w-md">{account.description}</p>
                              )}
                            </div>
                            
                            {isAssigned ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-slate-600 border-slate-200 cursor-not-allowed"
                                disabled
                              >
                                <CheckCircle className="h-4 w-4 mr-1" /> Already Assigned
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                                onClick={() => createBusinessChartOfAccount(account.id || 0)}
                                disabled={assigningAccounts[account.id || 0]}
                              >
                                {assigningAccounts[account.id || 0] ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-1" /> Assign
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
        setIsAddDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Chart of Account</DialogTitle>
            <DialogDescription>
              Create a new chart of account specific to your business.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateAccount} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">
                  Account Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="code"
                  placeholder="e.g., 1000"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                  className="font-mono"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  A unique code for this account (e.g., 1000 for Cash)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">
                  Account Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="e.g., Cash"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Descriptive name for the account
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">
                  Account Type <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.type} onValueChange={handleTypeChange} required>
                  <SelectTrigger id="type">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    {accountTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The primary classification of this account
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subtype">Subtype</Label>
                <Select 
                  value={formData.subtype} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, subtype: value }))}
                  disabled={!formData.type}
                >
                  <SelectTrigger id="subtype">
                    <SelectValue placeholder="Select subtype (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.type && accountSubtypes[formData.type]?.map((subtype) => (
                      <SelectItem key={subtype} value={subtype}>
                        {subtype}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Further classification within the account type
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter a description of the account purpose and usage"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="active">Active Status</Label>
                  <p className="text-sm text-muted-foreground">
                    Can be used for transactions
                  </p>
                </div>
                <Switch
                  id="active"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="default">Default Account</Label>
                  <p className="text-sm text-muted-foreground">
                    Will be used as a default option
                  </p>
                </div>
                <Switch
                  id="default"
                  checked={formData.defaultAccount}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, defaultAccount: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="allowSubAccounts">Allow Sub-Accounts</Label>
                  <p className="text-sm text-muted-foreground">
                    This account can have sub-accounts
                  </p>
                </div>
                <Switch
                  id="allowSubAccounts"
                  checked={formData.allowSubAccounts}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allowSubAccounts: checked }))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setIsAddDialogOpen(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Create Account
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
} 